// Shuffle helpers for photo slideshows (web twin of mobile/lib/shuffle.ts).
//
// Every auto-cycling hero / montage should show its photos in a random order
// and start on a random photo, so a fresh page load doesn't always open on the
// same first image. Math.random at mount → a fresh load reshuffles.

import { useRef } from 'react';

// Fisher-Yates. Returns a NEW array; the input is never mutated.
export function shuffle<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i];
    out[i] = out[j];
    out[j] = tmp;
  }
  return out;
}

// Shuffle a list ONCE per mount, re-shuffling only when the underlying set
// changes (keyed by length + first item). Stable across re-renders so a
// slideshow tick never reorders mid-cycle. A fresh page load remounts → new
// order + new first item. Generic so it works for photo-url lists and for
// arrays of richer items (montage tiles, candidate photos).
export function useShuffled<T>(items: T[], keyOf: (x: T) => string = String): T[] {
  const key = `${items.length}|${items.length ? keyOf(items[0]) : ''}`;
  const ref = useRef<{ key: string; out: T[] } | null>(null);
  if (!ref.current || ref.current.key !== key) {
    ref.current = { key, out: shuffle(items) };
  }
  return ref.current.out;
}
